import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { LocalDate } from "@js-joda/core";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";
import { ZIndex } from "config/zIndex";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { getDatePickerDisplayState } from "./datePicker.display.logic";
import { DatePickerInput } from "./datePickerInput";
import { DatePickerPopup } from "./datePickerPopup";
import {
  ButtonTriggerWrapper,
  Container,
  Label,
  Message,
  RequiredMark,
} from "./datePicker.styles";
import {
  DatePickerProps,
  DatePickerSize,
  DatePickerTrigger,
  SelectionMode,
} from "./datePicker.types";
import {
  buildCalendarDays,
  buildDateArray,
  DatePickerDateFormat,
  formatDateValue,
  getStockholmLocalDateNow,
  getCalendarLabels,
  isSameMonthYear,
  isWeekend,
  normalizeToFirstOfMonth,
  parseDayKey,
  parseInputDate,
  toDayKey,
} from "./datePicker.utils";
import { useNonWorkingDays } from "./hooks/useNonWorkingDays";
import { useDatePickerPositioning } from "./useDatePickerPositioning";
import { useDatePickerState } from "./useDatePickerState";

const addMonths = (base: LocalDate, deltaMonths: number): LocalDate => {
  const totalMonths = base.year() * 12 + (base.monthValue() - 1) + deltaMonths;
  const year = Math.floor(totalMonths / 12);
  const monthIndex = totalMonths - year * 12;
  return LocalDate.of(year, monthIndex + 1, 1);
};

export const DatePicker = (props: DatePickerProps): JSX.Element => {
  const { t } = useTranslation();
  const parentZIndex = useParentZIndex();

  const dateFormat: DatePickerDateFormat = props.dateFormat ?? "YYYY-MM-DD";
  const size: DatePickerSize = props.size ?? "default";
  const trigger: DatePickerTrigger = props.trigger ?? "input";
  const allowRange =
    Boolean(props.allowRangePicker) || Boolean(props.rangeOnly);

  const allowSingleModeMultiSelect = props.allowMultiSelectInSingleMode ?? true;

  const initialMode: SelectionMode = props.rangeOnly ? "range" : "single";
  const baseDate =
    props.value ?? props.restrictToMonth ?? getStockholmLocalDateNow();
  const {
    containerRef,
    dispatch,
    popupRef,
    state: datePickerState,
    triggerRef,
  } = useDatePickerState(baseDate, initialMode);
  const buttonTriggerRef = useRef<HTMLDivElement>(null);

  const minDate = useMemo(
    () => props.earliestSelectableDate ?? null,
    [props.earliestSelectableDate],
  );
  const isAllowedByMin = (d: LocalDate): boolean => {
    if (!minDate) return true;
    return !d.isBefore(minDate);
  };

  const monthlyDay =
    typeof props.monthlySelectableDay === "number" &&
    props.monthlySelectableDay >= 1 &&
    props.monthlySelectableDay <= 31
      ? props.monthlySelectableDay
      : null;
  const matchesMonthlyDay = (d: LocalDate): boolean =>
    monthlyDay ? d.dayOfMonth() === monthlyDay : true;

  const restrictSameMonthRange = props.restrictToMonth != null;

  const isControlledHoliday = typeof props.excludeHolidays === "boolean";
  const isControlledWeekends = typeof props.excludeWeekends === "boolean";
  const legacyExclude =
    !isControlledHoliday &&
    !isControlledWeekends &&
    props.showExcludeToggles !== true
      ? Boolean(props.excludeNonWorkingDays)
      : false;

  const excludeHolidaysValue =
    (isControlledHoliday
      ? Boolean(props.excludeHolidays)
      : datePickerState.localExcludeHolidays) || legacyExclude;
  const excludeWeekendsValue =
    (isControlledWeekends
      ? Boolean(props.excludeWeekends)
      : datePickerState.localExcludeWeekends) || legacyExclude;

  const year = datePickerState.currentMonth.year();
  const nonWorkSet = useNonWorkingDays({
    companyId: props.companyId ?? null,
    year,
    includeWeekends: Boolean(excludeWeekendsValue),
  });

  const shouldExclude = (d: LocalDate): boolean => {
    if (!allowRange || datePickerState.mode !== "range") return false;
    if (excludeWeekendsValue && isWeekend(d)) return true;
    if (excludeHolidaysValue && nonWorkSet.has(toDayKey(d))) return true;
    return false;
  };

  const days = useMemo(() => {
    return buildCalendarDays(datePickerState.currentMonth);
  }, [datePickerState.currentMonth]);

  const calendarLabels = useMemo(() => getCalendarLabels(t), [t]);

  const todayKey = useMemo(() => toDayKey(getStockholmLocalDateNow()), []);
  const valueKey = useMemo(
    () => (props.value ? toDayKey(props.value) : null),
    [props.value],
  );
  const rangeStartKey = useMemo(
    () =>
      datePickerState.range.start
        ? toDayKey(datePickerState.range.start)
        : null,
    [datePickerState.range.start],
  );
  const rangeEndKey = useMemo(
    () =>
      datePickerState.range.end ? toDayKey(datePickerState.range.end) : null,
    [datePickerState.range.end],
  );
  const pendingStartKey = useMemo(
    () =>
      datePickerState.pendingStart
        ? toDayKey(datePickerState.pendingStart)
        : null,
    [datePickerState.pendingStart],
  );

  const placeholderText = t("DatePicker.Placeholder");
  const resolvedErrorText =
    props.errorText ?? t("DesignComponents.DefaultError");
  const displayState = getDatePickerDisplayState({
    allowRange,
    allowSingleModeMultiSelect,
    committedMultiKeys: datePickerState.committedMultiKeys,
    committedRangeEnd: datePickerState.committedRange.end,
    committedRangeStart: datePickerState.committedRange.start,
    dateFormat,
    disabled: props.disabled,
    draftValue: datePickerState.draftValue,
    hasError: props.hasError,
    hintText: props.hintText,
    isFocused: datePickerState.isFocused,
    isOpen: datePickerState.isOpen,
    mode: datePickerState.mode,
    multiSelectedKeys: datePickerState.multiSelectedKeys,
    rangeEnd: datePickerState.range.end,
    rangeStart: datePickerState.range.start,
    resolvedErrorText,
    value: props.value,
  });

  const showModeButtons = allowRange && !props.rangeOnly;
  const showHolidayWeekendToggles =
    allowRange &&
    datePickerState.mode === "range" &&
    props.showExcludeToggles === true;
  const showClearButton =
    Boolean(props.allowClear) && !props.disabled && displayState.isFilled;

  const canConfirm = useMemo(() => {
    if (datePickerState.mode === "range")
      return Boolean(datePickerState.range.start && datePickerState.range.end);
    if (allowRange && allowSingleModeMultiSelect)
      return datePickerState.multiSelectedKeys.size > 0 || Boolean(props.value);
    if (allowRange) return Boolean(props.value);
    return Boolean(props.value);
  }, [
    allowRange,
    allowSingleModeMultiSelect,
    datePickerState.mode,
    datePickerState.multiSelectedKeys,
    props.value,
    datePickerState.range,
  ]);

  const handleConfirm = (): void => {
    if (props.disabled || !canConfirm) return;
    let selectedDates: LocalDate[] = [];
    if (
      datePickerState.mode === "range" &&
      datePickerState.range.start &&
      datePickerState.range.end
    ) {
      selectedDates = buildDateArray(
        datePickerState.range.start,
        datePickerState.range.end,
      ).filter(
        (d) => isAllowedByMin(d) && !shouldExclude(d) && matchesMonthlyDay(d),
      );
    } else if (allowRange) {
      const keys = Array.from(datePickerState.multiSelectedKeys);
      const datesFromKeys =
        keys.length > 0
          ? keys.map(parseDayKey).sort((a, b) => a.compareTo(b))
          : [];
      const base =
        datesFromKeys.length > 0
          ? datesFromKeys
          : props.value
            ? [props.value]
            : [];
      selectedDates = base.filter(
        (d) => isAllowedByMin(d) && !shouldExclude(d) && matchesMonthlyDay(d),
      );
    }

    if (datePickerState.mode === "range") {
      if (datePickerState.range.start && datePickerState.range.end) {
        dispatch({
          type: "set-committed-range",
          committedRange: {
            start: datePickerState.range.start,
            end: datePickerState.range.end,
          },
        });
        dispatch({
          type: "set-committed-multi",
          committedMultiKeys: new Set(),
        });
      }
    } else if (allowRange) {
      dispatch({
        type: "set-committed-multi",
        committedMultiKeys: new Set(selectedDates.map((d) => toDayKey(d))),
      });
      dispatch({
        type: "set-committed-range",
        committedRange: { start: null, end: null },
      });
    }

    if (props.onRangeChange) {
      props.onRangeChange(selectedDates);
    } else if (!allowRange) {
      props.onChange?.(selectedDates[0] ?? null);
    }

    dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const closePicker = (commitIfPossible: boolean): void => {
    if (commitIfPossible && allowRange && canConfirm) {
      handleConfirm();
      return;
    }
    dispatch({ type: "set-open", isOpen: false });
    dispatch({ type: "set-focused", isFocused: false });
    props.onBlur?.();
  };

  const handleToggleOpen = (): void => {
    if (props.disabled) return;
    if (datePickerState.isOpen) {
      closePicker(true);
      return;
    }
    dispatch({ type: "set-open", isOpen: true });
    props.onFocus?.();
  };

  const handleFocus = (): void => {
    if (props.disabled) return;
    dispatch({ type: "set-focused", isFocused: true });
    props.onFocus?.();
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    dispatch({ type: "set-draft-value", draftValue: event.target.value });
  };

  const handleInputBlur = (): void => {
    dispatch({ type: "set-focused", isFocused: false });
    if (props.disabled || allowRange) return;
    const parsed = parseInputDate(datePickerState.draftValue, dateFormat);
    if (!parsed) {
      dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
      props.onBlur?.();
      return;
    }
    if (!isAllowedByMin(parsed) || !matchesMonthlyDay(parsed)) {
      dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
      props.onBlur?.();
      return;
    }
    props.onChange?.(parsed);
    dispatch({
      type: "set-draft-value",
      draftValue: formatDateValue(parsed, dateFormat),
    });
    dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleClear = (event?: React.MouseEvent): void => {
    if (!props.allowClear || props.disabled) return;
    event?.stopPropagation();
    dispatch({ type: "reset-selection" });
    if (allowRange) {
      if (props.onRangeChange) props.onRangeChange([]);
      else props.onChange?.(null);
    } else {
      props.onChange?.(null);
    }
    dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleDateSelectSingleImmediate = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;
    props.onChange?.(date);
    dispatch({ type: "set-open", isOpen: false });
    dispatch({
      type: "set-draft-value",
      draftValue: formatDateValue(date, dateFormat),
    });
    props.onBlur?.();
  };

  const handleDateSelectSingleInRangePicker = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;
    props.onRangeChange?.([date]);
    dispatch({
      type: "set-committed-range",
      committedRange: { start: date, end: date },
    });
    dispatch({
      type: "set-committed-multi",
      committedMultiKeys: new Set([toDayKey(date)]),
    });
    dispatch({ type: "set-open", isOpen: false });
    props.onBlur?.();
  };

  const handleDateSelectRange = (date: LocalDate): void => {
    if (props.disabled) return;
    if (!isAllowedByMin(date)) return;
    if (!matchesMonthlyDay(date)) return;

    if (!datePickerState.pendingStart) {
      dispatch({ type: "set-pending-start", pendingStart: date });
      dispatch({ type: "set-range", range: { start: date, end: null } });
      return;
    }

    if (
      restrictSameMonthRange &&
      !isSameMonthYear(date, datePickerState.pendingStart)
    ) {
      dispatch({ type: "set-pending-start", pendingStart: date });
      dispatch({ type: "set-range", range: { start: date, end: null } });
      return;
    }

    const start = datePickerState.pendingStart;
    if (date.isBefore(start)) {
      dispatch({ type: "set-range", range: { start: date, end: start } });
    } else {
      dispatch({ type: "set-range", range: { start, end: date } });
    }
    dispatch({ type: "set-pending-start", pendingStart: null });
  };

  const handleToggleMode = (next: SelectionMode): void => {
    dispatch({ type: "set-mode", mode: next });
    dispatch({ type: "set-pending-start", pendingStart: null });
    dispatch({ type: "set-range", range: { start: null, end: null } });
    if (next === "range") {
      dispatch({
        type: "set-multi-selected",
        multiSelectedKeys: new Set(),
      });
    }
  };

  const handleDateSelect = (date: LocalDate): void => {
    if (datePickerState.mode === "range") {
      handleDateSelectRange(date);
      return;
    }
    if (allowRange) {
      if (!allowSingleModeMultiSelect) {
        handleDateSelectSingleInRangePicker(date);
        return;
      }
      dispatch({
        type: "set-multi-selected",
        multiSelectedKeys: (() => {
          const next = new Set(datePickerState.multiSelectedKeys);
          const key = toDayKey(date);
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        })(),
      });
      return;
    }
    handleDateSelectSingleImmediate(date);
  };

  const restrictMonth = props.restrictToMonth
    ? normalizeToFirstOfMonth(props.restrictToMonth)
    : null;

  const isNavigationLocked =
    Boolean(restrictMonth) && Boolean(props.disableNavigationOutsideMonth);
  const isPrevDisabled = isNavigationLocked;
  const isNextDisabled = isNavigationLocked;

  useEffect(() => {
    if (props.value) {
      dispatch({
        type: "set-current-month",
        currentMonth: normalizeToFirstOfMonth(props.value),
      });
    }
  }, [props.value, dispatch]);

  useEffect(() => {
    if (props.rangeOnly) {
      dispatch({ type: "set-mode", mode: "range" });
    }
  }, [props.rangeOnly, dispatch]);

  useEffect(() => {
    if (!datePickerState.isOpen) return;
    dispatch({ type: "sync-from-committed" });
  }, [dispatch, datePickerState.isOpen]);

  useEffect(() => {
    if (!props.disabled || !datePickerState.isOpen) return;
    dispatch({ type: "set-open", isOpen: false });
    dispatch({ type: "set-focused", isFocused: false });
  }, [dispatch, datePickerState.isOpen, props.disabled]);

  useEffect(() => {
    if (!datePickerState.isFocused) {
      dispatch({
        type: "set-draft-value",
        draftValue: formatDateValue(props.value ?? null, dateFormat),
      });
    }
  }, [dateFormat, dispatch, datePickerState.isFocused, props.value]);

  useOnClickOutside(
    [containerRef, popupRef],
    () => {
      closePicker(true);
    },
    { enabled: datePickerState.isOpen, eventType: "mousedown" },
  );

  useDatePickerPositioning({
    buttonTriggerRef,
    dispatch: dispatch,
    isOpen: datePickerState.isOpen,
    menuAlignment: props.menuAlignment,
    popupRef: popupRef,
    trigger,
    triggerRef: triggerRef,
  });

  const menu = (
    <DatePickerPopup
      ref={popupRef}
      position={datePickerState.menuPosition}
      zIndex={parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown}
      size={size}
      mode={datePickerState.mode}
      showModeButtons={showModeButtons}
      showHolidayWeekendToggles={showHolidayWeekendToggles}
      allowRange={allowRange}
      allowClear={Boolean(props.allowClear)}
      canConfirm={canConfirm}
      currentMonth={datePickerState.currentMonth}
      monthNames={calendarLabels.monthNames}
      dayHeaders={calendarLabels.dayHeaders}
      days={days}
      todayKey={todayKey}
      valueKey={valueKey}
      rangeStartKey={rangeStartKey}
      rangeEndKey={rangeEndKey}
      pendingStartKey={pendingStartKey}
      pendingStart={datePickerState.pendingStart}
      multiSelectedKeys={datePickerState.multiSelectedKeys}
      restrictSameMonthRange={restrictSameMonthRange}
      excludeHolidaysValue={excludeHolidaysValue}
      excludeWeekendsValue={excludeWeekendsValue}
      isControlledHoliday={isControlledHoliday}
      isControlledWeekends={isControlledWeekends}
      excludeHolidaysLabel={t("DatePicker.ExcludeHolidays")}
      excludeWeekendsLabel={t("DatePicker.ExcludeSaturdayAndSunday")}
      notSelectableLabel={t("DatePicker.NotSelectable")}
      modeLabel={t("DatePicker.Mode")}
      singleLabel={t("DatePicker.Single")}
      rangeLabel={t("DatePicker.Range")}
      clearLabel={t("DatePicker.Clear")}
      chooseLabel={t("DatePicker.Choose")}
      onToggleMode={handleToggleMode}
      onPrevMonth={() => {
        dispatch({
          type: "set-current-month",
          currentMonth: addMonths(datePickerState.currentMonth, -1),
        });
      }}
      onNextMonth={() => {
        dispatch({
          type: "set-current-month",
          currentMonth: addMonths(datePickerState.currentMonth, 1),
        });
      }}
      onSelectDay={handleDateSelect}
      onClear={handleClear}
      onConfirm={handleConfirm}
      onExcludeHolidaysChange={props.onExcludeHolidaysChange}
      onExcludeWeekendsChange={props.onExcludeWeekendsChange}
      onExcludeNonWorkingDaysChange={props.onExcludeNonWorkingDaysChange}
      onSetLocalExcludeHolidays={(checked) =>
        dispatch({ type: "set-local-exclude-holidays", value: checked })
      }
      onSetLocalExcludeWeekends={(checked) =>
        dispatch({ type: "set-local-exclude-weekends", value: checked })
      }
      isAllowedByMin={isAllowedByMin}
      matchesMonthlyDay={matchesMonthlyDay}
      shouldExclude={shouldExclude}
      isPrevDisabled={isPrevDisabled}
      isNextDisabled={isNextDisabled}
    />
  );

  return (
    <Container ref={containerRef} $size={size} $trigger={trigger}>
      {trigger === "button" ? (
        <>
          {props.label && (
            <Label>
              {props.label}
              {props.required && <RequiredMark>*</RequiredMark>}
            </Label>
          )}
          <ButtonTriggerWrapper ref={buttonTriggerRef}>
            <PrimaryButton
              aria-expanded={datePickerState.isOpen}
              aria-haspopup="dialog"
              disabled={props.disabled}
              iconName={props.triggerIconName}
              label={props.triggerLabel ?? props.label ?? t("DatePicker.Open")}
              onClick={handleToggleOpen}
              size={size === "compact" ? "small" : "large"}
            />
          </ButtonTriggerWrapper>
          {displayState.messageText && (
            <Message $state={displayState.visualState}>
              {displayState.messageText}
            </Message>
          )}
        </>
      ) : (
        <DatePickerInput
          labelText={props.label}
          required={props.required}
          size={size}
          visualState={displayState.visualState}
          isOpen={datePickerState.isOpen}
          value={
            allowRange ? displayState.showValue : datePickerState.draftValue
          }
          placeholderText={placeholderText}
          disabled={props.disabled}
          readOnly={allowRange}
          showClearButton={showClearButton}
          messageText={displayState.messageText}
          onClear={handleClear}
          onToggleOpen={handleToggleOpen}
          onFocus={handleFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          inputRef={triggerRef}
          clearLabel={t("DatePicker.Clear")}
          openLabel={t("DatePicker.Open")}
        />
      )}
      {datePickerState.isOpen && typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : null}
    </Container>
  );
};
